import React, { Dispatch, SetStateAction, useRef, useState } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Avatar, AvatarGroup, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { PsychologyReverse } from "assets/material-custom-icons";
import { IPickedFile } from "components/modals/Files-picker/hooks/files-picker-context";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DocumentSectionType } from "constants/documents";
import { useActionHook } from "hooks";
import { useDealActions, useDealIdSelector, usePickUserPermissions, useUserSelector } from "store";
import { DocumentQuestion, DocumentSection, DocumentSectionSharedUser, UserPermissionType } from "store/types";
import { isRoleAnalyst } from "utils/user";

import { RoleName } from "../../../constants";
import { useContentContext } from "../Content/content-context";
import { QuestionSectionContextMenu } from "../Menus/QuestionSectionContextMenu";
import { QuestionsSet } from "../Questions/QuestionsSet";

interface QuestionSectionProps {
  documentId: number;
  id: number;
  sharedUsers: DocumentSectionSharedUser[];
  onShare: () => void;
  onAskAi: () => void;
  onFocus?: () => void;
  setPendingAIQuestionIds: Dispatch<SetStateAction<number[]>>;
  pendingAIQuestionIds: number[];
  questions: DocumentQuestion[];
  section: DocumentSection;
}

export const QuestionSection: React.FC<QuestionSectionProps> = ({
  documentId,
  id,
  sharedUsers,
  onShare,
  onAskAi,
  onFocus,
  setPendingAIQuestionIds,
  pendingAIQuestionIds,
  questions,
  section
}) => {
  const { deleteSection, updateSection, createQuestion, deleteQuestion, AIAnswerSection } = useContentContext();
  const { getFilesAction } = useDealActions();
  const { role } = useUserSelector();
  const dealId = useDealIdSelector();
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;
  const isReadOnlyUser =
    role?.name === RoleName.Guest || role?.name === RoleName.Viewer || role?.name === RoleName.TeamViewer;

  // const section = useDocumentSectionSelect({ documentId, sectionId: id });
  // const questions = useDocumentQuestionsSelect({ documentId, section_id: id });
  const menuRef: React.RefObject<SVGSVGElement> = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const { action: updateDocumentSection } = useActionHook(updateSection, {
    onSuccess: () => {
      onAskAi();
      setMenuOpen(false);
    }
  });

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(true);
  };
  const [isEditing, setIsEditing] = useState(false);
  const [titleValue, seTitleValue] = useState(section?.title);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    if (titleValue === section?.title) {
      setIsEditing(false);
      return;
    }
    if (titleValue?.trim() === "") {
      seTitleValue("Questionnaire"); // Revert to default if empty
    }
    updateSection({ id, title: titleValue });
    setIsEditing(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    seTitleValue(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleBlur(); // Save and exit edit mode
    } else if (event.key === "Escape") {
      seTitleValue(section?.title); // Revert unsaved changes
      setIsEditing(false);
    }
  };
  const generateAnswers = async () => {
    await updateSection({ id, type: DocumentSectionType.AIQuestion });
    await AIAnswerSection({
      file_ids: section?.ai_files?.map((file: IPickedFile) => file.id) || [],
      sectionId: id,
      documentId: documentId,
      send_ai: isSendAiAvailable //this flags means that section includes at least one question
    });
    dealId && (await getFilesAction(dealId));
  };

  const isSendAiAvailable = questions.some((question) => question.question);

  return (
    <Stack
      onFocus={onFocus}
      tabIndex={0}
      sx={{ margin: "1.5px", padding: "10px", boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.25)", flexGrow: 1 }}
      gap="10px"
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" gap="10px" alignItems="center">
          <Button
            disabled={
              section?.ai_status === "in-progress" || noAccessPermission || isViewerPermission || isReadOnlyUser
            }
            onClick={generateAnswers}
            sx={{
              padding: "1px 10px",
              minWidth: "auto",
              whiteSpace: "nowrap",
              background: "#DBE8F5",
              fontSize: "12px",
              "&:hover": {
                opacity: 0.8
              }
            }}
            variant="text"
            startIcon={<PsychologyReverse sx={{ fontSize: "16px" }} />}
          >
            Ask AI
          </Button>
          {!isReadOnlyUser && isEditing ? (
            <TextField
              value={titleValue}
              onChange={handleChange}
              onBlur={handleBlur} // Exit edit mode on blur (clicking away)
              onKeyDown={handleKeyDown} // Save on Enter, cancel on Escape
              autoFocus
              variant="standard"
            />
          ) : (
            <Typography sx={{ fontWeight: "500", color: "#0A3142" }} onClick={handleClick}>
              {titleValue}
            </Typography>
          )}
        </Stack>
        <Stack flexDirection="row">
          {Boolean(sharedUsers.length) && (
            <Stack>
              <AvatarGroup
                sx={{
                  "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 15 },
                  marginRight: "10px"
                }}
                max={4}
              >
                {sharedUsers.map((share: DocumentSectionSharedUser, index: number) => (
                  <MaterialTooltip isShow key={index} placement="top" title={share.user?.full_name}>
                    <Avatar src={share.user?.profile_photo_url} />
                  </MaterialTooltip>
                ))}
              </AvatarGroup>
            </Stack>
          )}

          {!noAccessPermission && !isViewerPermission && isRoleAnalyst({ role }) && !isReadOnlyUser && (
            <IconButton sx={{ padding: 0, width: "25px", height: "25px" }} onClick={handleContextMenu}>
              <MoreVertIcon ref={menuRef} />
            </IconButton>
          )}
        </Stack>
        <QuestionSectionContextMenu
          anchorEl={menuRef.current}
          hidden={noAccessPermission || isViewerPermission || isReadOnlyUser}
          onClose={() => setMenuOpen(false)}
          open={!!menuOpen}
          section={section}
          onDelete={() => {
            deleteSection({ sectionId: id });
            setMenuOpen(false);
          }}
          onAskAi={() => updateDocumentSection({ id, type: DocumentSectionType.AIQuestion })}
          onShare={() => {
            onShare();
            setMenuOpen(false);
          }}
        />
      </Stack>
      <QuestionsSet
        documentId={documentId}
        hideAddQuestion={!!noAccessPermission || !!isViewerPermission}
        questions={questions}
        createQuestion={() =>
          createQuestion({
            section_id: id,
            documentId
          })
        }
        deleteQuestion={deleteQuestion}
        setPendingAIQuestionIds={setPendingAIQuestionIds}
        pendingAIQuestionIds={pendingAIQuestionIds}
        section={section}
      />
    </Stack>
  );
};
